import {
  BulkLockModelDocument,
  type BulkLockModelMutation,
  type BulkLockModelMutationVariables,
  BulkUnlockModelDocument,
  type BulkUnlockModelMutation,
  type BulkUnlockModelMutationVariables,
  GetModelLockDocument,
  type GetModelLockQuery,
  type GetModelLockQueryVariables,
  LockModelDocument,
  type LockModelMutation,
  type LockModelMutationVariables,
  type ModelLockLockableTypeEnum,
  UnlockModelDocument,
  type UnlockModelMutation,
  type UnlockModelMutationVariables,
} from "@src/__generated__/graphql";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import type { BaseStore } from "@src/stores/BaseStore";
import { QueueState } from "@src/utils/mobx/states/queue-state";
import type { GraphQLFormattedError } from "graphql";
import { action, computed, makeObservable, observable } from "mobx";

export class ModelLockStore implements BaseStore {
  appStore: AppStore;

  private bulkLockMutator = new MutationHelper<
    BulkLockModelMutation,
    BulkLockModelMutationVariables
  >(BulkLockModelDocument);

  private bulkUnlockMutator = new MutationHelper<
    BulkUnlockModelMutation,
    BulkUnlockModelMutationVariables
  >(BulkUnlockModelDocument);

  private lockMutator = new MutationHelper<
    LockModelMutation,
    LockModelMutationVariables
  >(LockModelDocument);

  private unlockMutator = new MutationHelper<
    UnlockModelMutation,
    UnlockModelMutationVariables
  >(UnlockModelDocument);

  private modelLockFetcher = new FetchHelper<
    GetModelLockQuery,
    GetModelLockQueryVariables
  >(GetModelLockDocument);

  @observable
  private queue: QueueState<{
    lockable_id: string;
    lockable_type: ModelLockLockableTypeEnum;
  }>;

  constructor(appStore: AppStore) {
    this.appStore = appStore;
    this.queue = new QueueState(
      (a, b) =>
        a.lockable_id === b.lockable_id && a.lockable_type === b.lockable_type,
    );
    makeObservable(this);
  }

  @computed get isBulkLocking() {
    return this.bulkLockMutator.isLoading.value;
  }

  @computed get isBulkUnlocking() {
    return this.bulkUnlockMutator.isLoading.value;
  }

  @computed get isBulkLoading() {
    return this.isBulkLocking || this.isBulkUnlocking;
  }

  @computed
  get isLocking() {
    return (lockable_id: string, lockable_type: ModelLockLockableTypeEnum) =>
      this.queue.isQueued({ lockable_id, lockable_type }) &&
      this.lockMutator.isLoading.value;
  }

  @computed
  get isUnlocking() {
    return (lockable_id: string, lockable_type: ModelLockLockableTypeEnum) =>
      this.queue.isQueued({ lockable_id, lockable_type }) &&
      this.unlockMutator.isLoading.value;
  }

  @computed
  get isLoading() {
    return (
      lockable_id: string,
      lockable_type: ModelLockLockableTypeEnum,
    ): boolean =>
      this.isLocking(lockable_id, lockable_type) ||
      this.isUnlocking(lockable_id, lockable_type);
  }

  @action.bound async getModelLock(
    lockable_id: string,
    lockable_type: ModelLockLockableTypeEnum,
  ) {
    const [data, error] = await this.modelLockFetcher.fetch({
      lockable_id,
      lockable_type,
    });

    if (error || !data?.getModelLock) return null;

    return data.getModelLock;
  }

  @action.bound
  async bulkLockModel(
    lockable_ids: string[],
    lockable_type: ModelLockLockableTypeEnum,
  ) {
    return this.queue.usingAsync(
      lockable_ids.map((lockable_id) => ({ lockable_id, lockable_type })),
      () =>
        this.bulkLockMutator.mutate({
          lockable_ids,
          lockable_type,
        }),
    );
  }

  @action.bound async bulkUnlockModel(
    lockable_ids: string[],
    lockable_type: ModelLockLockableTypeEnum,
  ) {
    return this.queue.usingAsync(
      lockable_ids.map((lockable_id) => ({ lockable_id, lockable_type })),
      () =>
        this.bulkUnlockMutator.mutate({
          lockable_ids,
          lockable_type,
        }),
    );
  }

  @action.bound async lockModel(
    lockable_id: string,
    lockable_type: ModelLockLockableTypeEnum,
  ): Promise<
    [LockModelMutation | null, readonly GraphQLFormattedError[] | Error | null]
  > {
    return this.queue.usingAsync({ lockable_id, lockable_type }, () =>
      this.lockMutator.mutate({
        lockable_id,
        lockable_type,
      }),
    );
  }

  @action.bound async unlockModel(
    lockable_id: string,
    lockable_type: ModelLockLockableTypeEnum,
  ): Promise<
    [
      UnlockModelMutation | null,
      readonly GraphQLFormattedError[] | Error | null,
    ]
  > {
    return this.queue.usingAsync({ lockable_id, lockable_type }, () =>
      this.unlockMutator.mutate({
        lockable_id,
        lockable_type,
      }),
    );
  }
}
